import "./lib/install-storage-shim";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

// Detect Lovable preview / iframe contexts where service workers cause stale caches
const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
})();

const isPreviewHost =
  window.location.hostname.includes("id-preview--") ||
  window.location.hostname.includes("lovableproject.com") ||
  window.location.hostname.includes("lovable.app");

const swAllowed = "serviceWorker" in navigator && !isInIframe && !isPreviewHost;

if ("serviceWorker" in navigator && (isInIframe || isPreviewHost)) {
  // In preview/iframe: scrub any leftover SW + caches and force one clean reload
  (async () => {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      const cacheKeys = "caches" in window ? await caches.keys() : [];
      const hadStale = registrations.length > 0 || cacheKeys.length > 0;
      await Promise.all(registrations.map((r) => r.unregister().catch(() => false)));
      if ("caches" in window) {
        await Promise.all(cacheKeys.map((k) => caches.delete(k).catch(() => false)));
      }
      if (hadStale && !sessionStorage.getItem("__sw_cleanup_reload")) {
        sessionStorage.setItem("__sw_cleanup_reload", "1");
        window.location.reload();
      }
    } catch {
      // ignore
    }
  })();
}

if (swAllowed) {
  // Auto-reload exactly once when a new SW takes control
  let hasReloaded = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (hasReloaded) return;
    hasReloaded = true;
    window.location.reload();
  });

  // When a new SW is found and finishes installing, tell it to skip waiting
  // so it activates immediately instead of sitting idle until all tabs close.
  const wireUpdateFlow = (reg: ServiceWorkerRegistration) => {
    const promoteWaiting = () => {
      if (reg.waiting && navigator.serviceWorker.controller) {
        reg.waiting.postMessage({ type: "SKIP_WAITING" });
      }
    };
    promoteWaiting();
    reg.addEventListener("updatefound", () => {
      const installing = reg.installing;
      if (!installing) return;
      installing.addEventListener("statechange", () => {
        if (installing.state === "installed" && navigator.serviceWorker.controller) {
          promoteWaiting();
        }
      });
    });
  };

  navigator.serviceWorker.getRegistration().then((reg) => {
    if (reg) wireUpdateFlow(reg);
  }).catch(() => {});

  // Aggressively check for SW updates on focus / visibility change (critical for iOS PWAs)
  const checkForSWUpdate = () => {
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (reg) reg.update().catch(() => {});
    });
  };

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") checkForSWUpdate();
  });
  window.addEventListener("focus", checkForSWUpdate);

  setInterval(() => {
    if (document.visibilityState === "visible") checkForSWUpdate();
  }, 60_000);
}

const root = document.getElementById("root")!;
createRoot(root).render(<App />);
